import { BloombergMarketDataRequest } from 'marketData/MarketDataRequest';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { addItemToArrayWithMaxSize } from 'utils/arrays/arrayUtils';
import State from 'store/State';

export type BloombergMessage = {
    messageNumber: number;
    timestamp: number;
    eventType: string;
    response: unknown;
};

export type BloombergStatusMessage = {
    messageNumber: number;
    timestamp: number;
    eventType: string;
    status: string;
};

export type BloombergRequest = {
    requestNumber: number;
    uid: string;
    requestType: string;
    timestamp: number;
    request: BloombergMarketDataRequest;
    args: unknown;
    messages: BloombergMessage[];
    statuses: BloombergStatusMessage[];
};

export type BloombergState = {
    requests: BloombergRequest[];
    nextRequestNumber: number;
    nextMessageNumber: number;
};

export const initialBloombergState: BloombergState = {
    requests: [],
    nextRequestNumber: 1,
    nextMessageNumber: 1,
};
const bloombergSlice = createSlice({
    name: 'bloombergSlice',
    initialState: initialBloombergState,
    reducers: {
        addRequest: (
            state,
            action: PayloadAction<{
                uid: string;
                requestType: string;
                request: BloombergMarketDataRequest;
                args: unknown;
            }>
        ) => {
            const { uid, requestType, request, args } = action.payload;
            state.requests.push({
                requestNumber: state.nextRequestNumber++,
                uid,
                requestType,
                request,
                args,
                messages: [],
                statuses: [],
                timestamp: new Date().getTime(),
            });
        },
        recordMessage: (
            state,
            action: PayloadAction<{
                uid: string;
                requestType: string;
                eventType: string;
                response: unknown;
            }>
        ) => {
            const { uid, requestType, eventType, response } = action.payload;

            let request = state.requests.find(
                (r) => r.uid === uid && r.requestType === requestType
            );

            if (!request) {
                request = {
                    requestNumber: state.nextRequestNumber++,
                    uid,
                    requestType,
                    request: undefined as unknown as BloombergMarketDataRequest,
                    args: {},
                    messages: [],
                    statuses: [],
                    timestamp: new Date().getTime(),
                };
                state.requests.push(request);
            }

            request.messages = addItemToArrayWithMaxSize(
                {
                    messageNumber: state.nextMessageNumber++,
                    timestamp: new Date().getTime(),
                    eventType,
                    response,
                },
                request!.messages,
                100
            );
        },
        recordStatus: (
            state,
            action: PayloadAction<{
                uid: string;
                requestType: string;
                eventType: string;
                status: string;
            }>
        ) => {
            const { uid, requestType, eventType, status } = action.payload;

            const request = state.requests.find(
                (r) => r.uid === uid && r.requestType === requestType
            )!;

            request.statuses.push({
                messageNumber: state.nextMessageNumber++,
                timestamp: new Date().getTime(),
                eventType,
                status,
            });
        },
    },
});

export const { addRequest, recordMessage, recordStatus } =
    bloombergSlice.actions;

export const selectBloombergState = (state: State) => state.bloomberg;

export const bloombergReducer = bloombergSlice.reducer;
